import { computed } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import { getTimesheetSettings } from '../api/timesheetSettings';
import type { TimesheetSettings, LaborCodeItem } from '../api/timesheetSettings';
import { ApiClientError } from '../api/client';

// VP increment codes → numeric step value.
// 'Q' = quarter-hour, 'H' = half-hour, 'T' = tenth, 'W' = whole-hour.
// Anything unrecognised falls back to 0.25 (VP's most common default).
function incrementFromCode(code: string | undefined): number {
  switch (code?.toUpperCase()) {
    case 'H': return 0.5;
    case 'T': return 0.1;
    case 'W': return 1.0;
    case 'Q':
    default:  return 0.25;
  }
}

// Coerces the 'Y'/'N'/undefined/boolean showWbsN fields to a boolean.
// VP returns string 'Y'/'N'; any other truthy-non-N value is treated as shown.
function parseShow(raw: string | boolean | undefined, defaultVal = true): boolean {
  if (raw === undefined || raw === null) return defaultVal;
  if (typeof raw === 'boolean') return raw;
  return raw.trim().toUpperCase() !== 'N';
}

export function useTimesheetSettings() {
  const query = useQuery<TimesheetSettings, ApiClientError>({
    queryKey: ['timesheet-settings'],
    queryFn: getTimesheetSettings,
    retry: false,
    staleTime: 300_000,
  });

  const settings = computed(() => query.data.value ?? null);

  // WBS column labels with fallbacks to Vantagepoint defaults.
  function wbsLabel(n: 1 | 2 | 3): string {
    const s = settings.value;
    if (!s?.wbsLabels) {
      return n === 1 ? 'Project' : n === 2 ? 'Phase' : 'Task';
    }
    const raw = n === 1 ? s.wbsLabels.wbs1 : n === 2 ? s.wbsLabels.wbs2 : s.wbsLabels.wbs3;
    const fallback = n === 1 ? 'Project' : n === 2 ? 'Phase' : 'Task';
    return raw?.trim() || fallback;
  }

  const showWbs2 = computed(() => parseShow(settings.value?.showWbs2));
  const showWbs3 = computed(() => parseShow(settings.value?.showWbs3));
  const showFirmName = computed(() => parseShow(settings.value?.showFirmName || undefined));
  const showLaborCode = computed(() => parseShow(settings.value?.showLaborCode, false));
  // Editable only when VP explicitly allows it (ChangeDefaultLC === 'Y'); locked
  // to the default otherwise. Conservative default: absent/unknown ⇒ locked, so
  // we never present an editable control VP's config says the user can't use.
  const canEditLaborCode = computed(
    () => (settings.value?.changeLaborCode ?? '').trim().toUpperCase() === 'Y',
  );
  const showBillCategory = computed(() => parseShow(settings.value?.showBillCategory, false));

  const hoursIncrement = computed(() => incrementFromCode(settings.value?.increment));

  const laborCodes = computed((): LaborCodeItem[] => settings.value?.laborCodes ?? []);
  const defaultLaborCode = computed(() => settings.value?.defaultLaborCode ?? '');
  const defaultLaborCodeName = computed(() => settings.value?.defaultLaborCodeName ?? '');

  const companyName = computed(() => settings.value?.companyName ?? '');
  const multiCompanyEnabled = computed(() => settings.value?.multiCompanyEnabled ?? false);

  const currentPeriodEnd = computed(() => settings.value?.currentPeriodEnd ?? null);

  return {
    settings,
    wbsLabel,
    showWbs2,
    showWbs3,
    showFirmName,
    showLaborCode,
    canEditLaborCode,
    showBillCategory,
    hoursIncrement,
    laborCodes,
    defaultLaborCode,
    defaultLaborCodeName,
    companyName,
    multiCompanyEnabled,
    currentPeriodEnd,
  };
}
